import {
  ArrowRight,
  BookOpen,
  KeyRound,
  Radio,
  ShieldCheck,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { MethodChip } from "@components/MethodChip.tsx";
import { getBundle, guideHref, operationHref } from "@lib/content.ts";
import { guidesFor } from "@lib/guides.ts";

const HIGHLIGHTS = [
  {
    icon: KeyRound,
    title: "One token, every API",
    body: "A single client_credentials exchange returns an RS256 token accepted across racing data, wagering and account APIs.",
  },
  {
    icon: ShieldCheck,
    title: "Scoped and tenant-isolated",
    body: "Grants live in the token itself. You only ever see the tenants and data types your credentials carry.",
  },
  {
    icon: Radio,
    title: "Pull or push",
    body: "Poll REST endpoints for on-demand reads, or subscribe to a realtime stream and receive changes as they happen.",
  },
];

export function HomePage() {
  const { version = "" } = useParams();
  // Nav only — the landing page must not pull the full specification into the
  // initial bundle just to count operations.
  const { nav } = getBundle(version);
  const guides = guidesFor(version);
  const firstGuide = guides[0];

  const operationCount = nav.reduce(
    (total, service) =>
      total +
      service.groups.reduce((n, group) => n + group.operations.length, 0),
    0,
  );

  return (
    <div className="px-4 py-10 sm:px-7">
      <div className="max-w-[52rem]">
        <p className="mb-2 font-mono text-[12px] uppercase tracking-widest text-accent">
          {version} · {operationCount} operations
        </p>
        <h1 className="text-[34px] font-semibold leading-[1.15] tracking-tight text-ink sm:text-[40px]">
          Global Tote API reference
        </h1>
        <p className="mt-4 text-[16px] leading-relaxed text-muted">
          Everything you need to integrate with the Global Tote platform: racing
          and pool data, wager placement, transactions and realtime push. Every
          endpoint below is external and partner-facing, with request and
          response schemas, worked examples and copy-ready code in nine
          languages.
        </p>

        <div className="mt-6 flex flex-wrap gap-2.5">
          {firstGuide ? (
            <Link
              to={guideHref(version, firstGuide.slug)}
              className="inline-flex items-center gap-1.5 rounded-default bg-accent px-3.5 py-2 text-[14px] font-medium text-accent-contrast transition hover:bg-accent-hover"
            >
              <BookOpen className="size-4" aria-hidden />
              Start here
            </Link>
          ) : null}
          <a
            href={`/openapi/${version}.json`}
            download={`globaltote-openapi-${version}.json`}
            className="inline-flex items-center gap-1.5 rounded-default border border-line px-3.5 py-2 text-[14px] font-medium text-ink transition hover:bg-surface-2"
          >
            Download OpenAPI
          </a>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {HIGHLIGHTS.map((item) => (
            <div
              key={item.title}
              className="rounded-default border border-line bg-surface px-3.5 py-3"
            >
              <item.icon className="size-4 text-accent" aria-hidden />
              <h2 className="mt-2 text-[14px] font-semibold text-ink">
                {item.title}
              </h2>
              <p className="mt-1 text-[13px] leading-snug text-muted">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Service index -------------------------------------------------- */}
      <div className="mt-12 max-w-[52rem] space-y-8">
        {nav.map((service) => (
          <section key={service.id}>
            <h2 className="text-[18px] font-semibold tracking-tight text-ink">
              {service.title}
            </h2>
            {service.summary ? (
              <p className="mt-1 text-[13.5px] text-muted">{service.summary}</p>
            ) : null}

            <ul className="mt-3 divide-y divide-line rounded-default border border-line">
              {service.groups.flatMap((group) =>
                group.operations.map((operation) => (
                  <li key={operation.id}>
                    <Link
                      to={operationHref(version, operation.id)}
                      className="group flex items-center gap-3 px-3 py-2.5 transition hover:bg-surface"
                    >
                      <MethodChip method={operation.method} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-medium text-ink">
                          {operation.summary}
                        </span>
                        <span className="block truncate font-mono text-[11.5px] text-subtle">
                          {operation.path}
                        </span>
                      </span>
                      <ArrowRight
                        className="size-3.5 shrink-0 text-subtle opacity-0 transition group-hover:opacity-100"
                        aria-hidden
                      />
                    </Link>
                  </li>
                )),
              )}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
